import React, { useContext as useContextCore } from 'react'
import merge from 'lodash/merge'
import { useMeasure } from 'react-use'
import type {
  ListField,
  ListFieldConfig,
  UseListProps,
  UseListResult,
  UseListWithSelection,
} from '@ztechapps/common.hook'
import type { Chat, RouteDef } from '@ztechapps/common.contract'
import { StorageType, useListWithSelection, useUserTimeZone } from '@ztechapps/common.hook'
import { useChatMessageList } from '@ztechapps/chat.common'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { conversationInboxQuery } from './graphql'
import { useConversationListFieldConfig } from './useConversationListFieldConfig'

// The recipient option emitted by CustomerSelectCreatable — an existing customer or a typed-email
// "create" option. Only the fields the send path reads are modeled here.
export type RecipientOption = {
  _id?: string
  profile?: { name?: string; email?: string }
  company?: { name?: string }
}

export type ConversationInboxContextType = {
  chatList: UseListResult<Chat>
  chatListWithSelection: UseListWithSelection<Chat>
  onRefresh: () => Promise<void>
  /** The category the toolbar's saved filters live under — the `filterCategory` prop, else the `storageKey`. */
  filterCategory?: string
  /** Renders a row's document cell — provided by the host (its document links are product-specific). */
  renderDocumentCell: (chat: { document?: Chat['document']; metadata?: Chat['metadata'] }) => React.ReactNode
  /** The list's column configuration, its Assignee filter values loaded from the tenant's internal users. */
  fieldConfig: ListFieldConfig
  /** The conversation detail route the rows link to. */
  conversationItemRoute: RouteDef
  /** The recipient the New conversation form opens with; none leaves it empty. */
  defaultRecipient?: RecipientOption
}

const context = React.createContext<ConversationInboxContextType>({} as ConversationInboxContextType)

export type ConversationInboxProviderProps = {
  children: React.ReactNode
  /** Persists the list state (and, by default, the toolbar's saved filters) under this key; none persists nothing. */
  storageKey?: string
  /** The saved-filter category when it differs from `storageKey`. */
  filterCategory?: string
  /** Overrides for the list hook — `filter`, `sort`, `limit` and the like. */
  listProps?: Partial<UseListProps>
  /** Merged over the column configuration — `{ company: { isVisible: false } }` hides a column by default. */
  fieldConfig?: Record<string, Partial<ListField>>
  defaultRecipient?: RecipientOption
  renderDocumentCell?: ConversationInboxContextType['renderDocumentCell']
}

const defaultRenderDocumentCell: ConversationInboxContextType['renderDocumentCell'] = (chat) =>
  chat?.document?.code ?? '—'

export const ConversationInboxProvider = ({
  children,
  storageKey,
  filterCategory,
  listProps,
  fieldConfig,
  defaultRecipient,
  renderDocumentCell,
}: ConversationInboxProviderProps) => {
  const timeZone = useUserTimeZone()
  const listFieldConfig = merge({}, useConversationListFieldConfig(), fieldConfig)
  const conversationItemRoute = useInject<RouteDef>(WebuiIocKey.routes.conversationItem)
  const chatList = useChatMessageList({
    query: conversationInboxQuery,
    limit: 100,
    subscribeToUpdates: true,
    // List state persists only under `storageKey`, tested as useNotificationList.ts:19 in
    // libs/common/hook/src/notification: a persisted state replays its base `filter` scope into the
    // next record (same bleed: CompanyAccountingStatement.tsx:25-27 in apps/panelpunches/admin), and
    // `StorageType.none` with a non-empty key keeps the query skipped (useBuildListQueryOptions.ts:56-58
    // in libs/common/hook/src/useList).
    listActionOptions: {
      storageKey,
      storageType: storageKey ? StorageType.localStorage : StorageType.none,
      timeZone,
    },
    ...listProps,
  })
  const chatListWithSelection = useListWithSelection(chatList?.list)

  const onRefresh = async () => {
    await chatList.refetch()
  }

  return (
    <context.Provider
      value={{
        chatList,
        chatListWithSelection,
        onRefresh,
        filterCategory: filterCategory ?? storageKey,
        renderDocumentCell: renderDocumentCell ?? defaultRenderDocumentCell,
        fieldConfig: listFieldConfig,
        conversationItemRoute,
        defaultRecipient,
      }}
    >
      {children}
    </context.Provider>
  )
}

export const useConversationInboxContext = () => useContextCore(context)

export type InboxComposerContextType = {
  threadHeight: number
  // The title row's measured content height — the provider owns the measure and the thread layout
  // attaches the ref, so the composer's max height tracks the wrapped subject without recounting.
  titleRef: (element: HTMLDivElement) => void
  titleHeight: number
  // The page's own conversation read, re-read after a public reply: the reply moves the conversation
  // to Pending on the server, but the send response's `chat` carries no status
  // (EmailChatGateway.ts:146-149) and that read is cache-only once loaded (useBuildListQueryOptions.ts),
  // so the header's status badge only follows a re-read. The re-read runs even when the send
  // rejects: the server can persist the message and flip the status before a post-commit step
  // fails, leaving the mutation errored with the header a move behind.
  refetchChat: () => Promise<unknown>
}

const composerContext = React.createContext<InboxComposerContextType>({} as InboxComposerContextType)

export type InboxComposerProviderProps = {
  threadHeight: number
  refetchChat: () => Promise<unknown>
  children: React.ReactNode
}

// What the inbox composer holds beyond the reply mode (ConversationDetail keeps it, so it survives
// the ConversationProvider remount a conversation switch causes) and the note save
// (ConversationProvider keeps it, shared with the record pages): the thread's measured height and
// the page's conversation re-read, which only this mount owns.
export const InboxComposerProvider = ({ threadHeight, refetchChat, children }: InboxComposerProviderProps) => {
  const [titleRef, { height: titleHeight }] = useMeasure<HTMLDivElement>()
  return (
    <composerContext.Provider value={{ threadHeight, titleRef, titleHeight, refetchChat }}>
      {children}
    </composerContext.Provider>
  )
}

export const useInboxComposerContext = () => useContextCore(composerContext)
